import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const GFE_GIFT_WRAP_SELECTOR = 'input[data-gfe-gift-wrap-option]';
const GFE_SOURCE = 'gfe-gift-wrap';

function getRoot() {
  return window.Shopify?.routes?.root || '/';
}

function getSectionIds() {
  return [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean)
    ),
  ].slice(0, 5);
}

function getSectionsUrl() {
  return `${window.location.pathname}${window.location.search}`;
}

function getVisibleItemCount(cart) {
  return (cart.items || []).reduce((count, item) => {
    const isGiftWrap =
      item.properties?._gfe_gift_wrap === 'true' ||
      item.properties?._gfe_gift_wrap === true;

    return isGiftWrap ? count : count + Number(item.quantity || 0);
  }, 0);
}

async function requestJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok || data.status || data.errors) {
    throw new Error(
      data.description ||
        data.message ||
        (typeof data.errors === 'string' ? data.errors : '') ||
        'Gaveindpakningen kunne ikke opdateres.'
    );
  }

  return data;
}

async function addGiftWrap(variantId, parentLineKey, sectionIds) {
  return requestJson(`${getRoot()}cart/add.js`, {
    items: [
      {
        id: Number(variantId),
        quantity: 1,
        parent_line_key: parentLineKey,
        properties: {
          _gfe_gift_wrap: 'true',
        },
      },
    ],
    sections: sectionIds.join(','),
    sections_url: getSectionsUrl(),
  });
}

async function removeGiftWrap(childLineKey, sectionIds) {
  return requestJson(`${getRoot()}cart/change.js`, {
    id: childLineKey,
    quantity: 0,
    sections: sectionIds.join(','),
    sections_url: getSectionsUrl(),
  });
}

async function getCart() {
  const response = await fetch(`${getRoot()}cart.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Kurven kunne ikke genindlæses.');
  }

  return response.json();
}

function restoreSelection(fieldset, variantId) {
  for (const radio of fieldset.querySelectorAll(GFE_GIFT_WRAP_SELECTOR)) {
    radio.checked = radio.value === variantId;
  }
}

document.addEventListener('change', async (event) => {
  const input = event.target;

  if (
    !(input instanceof HTMLInputElement) ||
    !input.matches(GFE_GIFT_WRAP_SELECTOR) ||
    !input.checked
  ) {
    return;
  }

  const fieldset = input.closest('[data-gfe-gift-wrap]');

  if (!(fieldset instanceof HTMLFieldSetElement)) {
    return;
  }

  const cartItemsComponent = fieldset.closest('cart-items-component');
  const parentLineKey = fieldset.dataset.parentKey || '';
  const currentChildKey = fieldset.dataset.childKey || '';
  const currentVariantId = fieldset.dataset.currentVariantId || '';
  const selectedVariantId = input.value;

  if (!parentLineKey || selectedVariantId === currentVariantId) {
    return;
  }

  const errorElement = fieldset.querySelector('[data-gfe-gift-wrap-error]');
  const sectionIds = getSectionIds();
  const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

  let action = 'update';
  if (!currentChildKey && selectedVariantId) action = 'add';
  if (currentChildKey && !selectedVariantId) action = 'remove';

  (cartItemsComponent || fieldset).dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: selectedVariantId
        ? [{ merchandiseId: selectedVariantId, quantity: 1 }]
        : [{ id: currentChildKey, quantity: 0 }],
      promise: deferredUpdatePromise.promise,
    })
  );

  fieldset.disabled = true;
  fieldset.setAttribute('aria-busy', 'true');

  if (errorElement instanceof HTMLElement) {
    errorElement.hidden = true;
    errorElement.textContent = '';
  }

  try {
    let finalResponse;

    if (selectedVariantId) {
      finalResponse = await addGiftWrap(selectedVariantId, parentLineKey, sectionIds);

      if (currentChildKey) {
        finalResponse = await removeGiftWrap(currentChildKey, sectionIds);
      }
    } else if (currentChildKey) {
      finalResponse = await removeGiftWrap(currentChildKey, sectionIds);
    } else {
      deferredUpdatePromise.reject(new Error('Ingen ændring af gaveindpakning.'));
      return;
    }

    const cart = await getCart();

    if (cartItemsComponent instanceof HTMLElement) {
      const currentSectionId = cartItemsComponent.dataset.sectionId || '';
      const currentSectionHtml = finalResponse?.sections?.[currentSectionId];

      if (currentSectionId && currentSectionHtml) {
        await morphSection(currentSectionId, currentSectionHtml, {
          mode: cartItemsComponent.hasAttribute('data-drawer') ? 'hydration' : 'full',
        });
      }
    }

    const eventCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
    eventCart.totalQuantity = getVisibleItemCount(cart);

    deferredUpdatePromise.resolve({
      cart: eventCart,
      detail: {
        source: GFE_SOURCE,
        items: cart.items,
        itemCount: getVisibleItemCount(cart),
        sections: finalResponse?.sections || {},
        didError: false,
      },
    });
  } catch (error) {
    restoreSelection(fieldset, currentVariantId);
    deferredUpdatePromise.reject(error);

    if (errorElement instanceof HTMLElement) {
      errorElement.textContent =
        error instanceof Error
          ? error.message
          : 'Gaveindpakningen kunne ikke opdateres.';
      errorElement.hidden = false;
    }

    console.error(error);
  } finally {
    if (fieldset.isConnected) {
      fieldset.disabled = false;
      fieldset.removeAttribute('aria-busy');
    }
  }
});
